import React, { useState, useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Check, ArrowRight, Sparkles, Target, TrendingUp, Zap } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Text Reveal Animation
const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Audit = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    vertical: '',
    challenges: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/audit/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          website: formData.website,
          vertical: formData.vertical,
          challenges: formData.challenges,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setIsSubmitted(true);
        toast.success(data.message || 'Request submitted! We\'ll send your audit within 48 hours.');
      } else {
        toast.error(data.detail || 'Failed to submit request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting audit form:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Target, title: 'Detailed Analysis', desc: 'In-depth review of your current strategies' },
    { icon: TrendingUp, title: 'Growth Opportunities', desc: 'Identify untapped potential and quick wins' },
    { icon: Sparkles, title: 'Expert Recommendations', desc: 'Actionable insights from our specialists' },
    { icon: Zap, title: 'Priority Roadmap', desc: 'Clear next steps ranked by impact' }
  ];

  const auditTypes = [
    {
      title: 'Digital Marketing Audit',
      includes: ['SEO Performance Analysis', 'Social Media Review', 'Paid Ads Evaluation', 'Content Strategy Assessment', 'Competitor Analysis']
    },
    {
      title: 'Brand & Design Audit',
      includes: ['Visual Identity Review', 'Brand Consistency Check', 'User Experience Analysis', 'Design System Evaluation', 'Market Positioning']
    },
    {
      title: 'Technical Audit',
      includes: ['Website Performance', 'Mobile Responsiveness', 'Security Assessment', 'Code Quality Review', 'Infrastructure Analysis']
    }
  ];

  return (
    <Layout>
      {/* HERO SECTION */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-sunrise-hero relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 left-[20%] w-[500px] h-[500px] sun-orb sun-orb-primary"
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div 
            className="absolute bottom-20 right-[15%] w-[300px] h-[300px] sun-orb sun-orb-soft"
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 10, repeat: Infinity, delay: 2 }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <TextReveal>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg shadow-orange-500/10 mb-6">
                <motion.span 
                  className="w-2 h-2 bg-[#FF6B35] rounded-full"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span className="text-sm font-medium text-[#5A5A5A]">Free Strategic Audit</span>
              </div>
            </TextReveal>
            
            <TextReveal delay={0.1}>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-[1.05] mb-4">
                Get a free
              </h1>
            </TextReveal>
            <TextReveal delay={0.15}>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">
                <span className="text-gradient-sunrise">growth audit.</span>
              </h1>
            </TextReveal>
            
            <TextReveal delay={0.2}>
              <p className="text-lg md:text-xl text-[#5A5A5A] max-w-2xl leading-relaxed">
                Our specialists will analyze your current strategies and provide actionable 
                recommendations to accelerate your growth—completely free.
              </p>
            </TextReveal>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-12 bg-white border-y border-[#FF6B35]/10">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <TextReveal key={benefit.title} delay={index * 0.1}>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF6B35]/10 to-[#FFB627]/10 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="text-[#FF6B35]" size={26} />
                  </div>
                  <h3 className="text-[#2D2D2D] font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-[#5A5A5A] text-sm">{benefit.desc}</p>
                </div>
              </TextReveal>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="section-padding bg-[#FFFBF5]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              {!isSubmitted ? (
                <>
                  <TextReveal>
                    <h2 className="font-display text-3xl text-[#2D2D2D] mb-2">Request Your Free Audit</h2>
                    <p className="text-[#5A5A5A] mb-8">Fill in the details below and we'll get back to you within 48 hours.</p>
                  </TextReveal>
                  
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="audit-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[#5A5A5A] text-sm mb-2">Your Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          data-testid="audit-name"
                          className="input-warm"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-[#5A5A5A] text-sm mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          data-testid="audit-email"
                          className="input-warm"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[#5A5A5A] text-sm mb-2">Company Name</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          data-testid="audit-company"
                          className="input-warm"
                          placeholder="Your Company"
                        />
                      </div>
                      <div>
                        <label className="block text-[#5A5A5A] text-sm mb-2">Website URL</label>
                        <input
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          data-testid="audit-website"
                          className="input-warm"
                          placeholder="https://yourwebsite.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#5A5A5A] text-sm mb-2">What area needs the most attention? *</label>
                      <select
                        name="vertical"
                        value={formData.vertical}
                        onChange={handleChange}
                        required
                        data-testid="audit-vertical"
                        className="input-warm cursor-pointer"
                      >
                        <option value="">Select an area...</option>
                        <option value="digital-marketing">Digital Marketing</option>
                        <option value="brand-design">Brand & Design</option>
                        <option value="photography">Photography</option>
                        <option value="web-development">Web & App Development</option>
                        <option value="multiple">Multiple Areas</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[#5A5A5A] text-sm mb-2">What are your biggest challenges?</label>
                      <textarea
                        name="challenges"
                        value={formData.challenges}
                        onChange={handleChange}
                        rows={4}
                        data-testid="audit-challenges"
                        className="input-warm resize-none"
                        placeholder="Tell us about your current situation and what you'd like to improve..."
                      />
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="audit-submit"
                      className="btn-sunrise px-8 py-4 w-full justify-center disabled:opacity-50"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <span className="flex items-center gap-2">
                        {isSubmitting ? (
                          <>
                            <motion.div 
                              className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Request Free Audit
                            <ArrowRight size={18} />
                          </>
                        )}
                      </span>
                    </motion.button>
                  </form>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                >
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-orange-500/20">
                    <Check className="text-white" size={40} />
                  </div>
                  <h2 className="font-display text-3xl text-[#2D2D2D] mb-4">Request Submitted!</h2>
                  <p className="text-[#5A5A5A] max-w-md mx-auto">
                    Thank you for your interest. Our team will review your information and 
                    send your personalized audit within 48 hours.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Sidebar - What's Included */}
            <div className="lg:col-span-5 space-y-6">
              {auditTypes.map((audit, index) => (
                <TextReveal key={audit.title} delay={index * 0.1}>
                  <div className="p-6 bg-white rounded-2xl border border-[#FF6B35]/10 shadow-sm">
                    <h3 className="font-display text-lg text-[#2D2D2D] mb-4">{audit.title}</h3>
                    <ul className="space-y-3">
                      {audit.includes.map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-[#5A5A5A] text-sm">
                          <Check size={16} className="text-[#FF6B35] flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </TextReveal>
              ))}

              {/* Trust Badge */}
              <TextReveal delay={0.3}>
                <div className="p-6 rounded-2xl bg-gradient-to-br from-[#FF6B35]/10 to-[#FFB627]/10 border border-[#FF6B35]/20">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/20">
                      <Sparkles className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="text-[#2D2D2D] font-semibold mb-2">No Strings Attached</h4>
                      <p className="text-[#5A5A5A] text-sm leading-relaxed">
                        This audit is completely free with no obligation. We believe in 
                        proving our value before asking for commitment.
                      </p>
                    </div>
                  </div>
                </div>
              </TextReveal>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Audit;
